import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ContentPreviewModal = ({ content, onClose, isFavorite, onToggleFavorite }) => {
  const getContentIcon = (contentType) => {
    const icons = {
      email_template: 'Mail',
      ad_copy: 'TrendingUp',
      video_script: 'Video',
      report: 'FileText',
      checklist: 'CheckSquare',
      analytics_report: 'BarChart3'
    };
    return icons?.[contentType] || 'FileText';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const getPerformanceColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-warning';
    return 'text-error';
  };

  const thumbnailUrl = content?.metadata?.thumbnail_url || 'https://api.dicebear.com/7.x/shapes/svg?seed=' + content?.id;
  const performanceScore = content?.metadata?.performance_score || 0;
  const industry = content?.metadata?.industry || 'General';
  const sourceUrl = content?.metadata?.source_url;
  const metrics = content?.metadata?.performance_metrics || {};

  const handleCopyContent = () => {
    navigator?.clipboard?.writeText(content?.content);
    // Would show toast notification in real implementation
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div className="bg-card rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
        onClick={(e) => e?.stopPropagation()}
      >
        {/* Header */}
        <div className="relative">
          <div className="h-48 bg-muted overflow-hidden">
            <img
              src={thumbnailUrl}
              alt={`Preview of ${content?.title}`}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = 'https://api.dicebear.com/7.x/shapes/svg?seed=' + content?.id;
              }}
            />
          </div>
          <div className="absolute top-4 right-4 flex items-center gap-2">
            <button
              onClick={() => onToggleFavorite(content?.id)}
              className="p-2 rounded-lg bg-card/90 backdrop-blur-sm hover:bg-card transition-colors"
              title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            >
              <Icon
                name={isFavorite ? 'Heart' : 'Heart'}
                size={20}
                color={isFavorite ? 'var(--color-error)' : 'var(--color-muted-foreground)'}
                fill={isFavorite ? 'var(--color-error)' : 'none'}
              />
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg bg-card/90 backdrop-blur-sm hover:bg-card transition-colors"
            >
              <Icon name="X" size={20} color="var(--color-muted-foreground)" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-192px)]">
          {/* Title and Meta */}
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Icon name={getContentIcon(content?.content_type)} size={24} color="var(--color-primary)" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold text-foreground mb-2">{content?.title}</h2>
              <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Icon name={getContentIcon(content?.content_type)} size={14} />
                  {content?.content_type?.replace('_', ' ')}
                </span>
                <span>•</span>
                <span>{industry}</span>
                <span>•</span>
                <span>{formatDate(content?.created_at)}</span>
              </div>
            </div>
          </div>

          {/* Performance Metrics */}
          {performanceScore > 0 && (
            <div className="bg-muted rounded-xl p-4 mb-6">
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="BarChart3" size={16} />
                Performance Analytics
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Overall Score</p>
                  <p className={`text-2xl font-bold ${getPerformanceColor(performanceScore)}`}>
                    {performanceScore}%
                  </p>
                </div>
                {metrics?.engagement_rate && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Engagement Rate</p>
                    <p className="text-2xl font-bold text-foreground">{metrics?.engagement_rate}%</p>
                  </div>
                )}
                {metrics?.conversion_rate && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Conversion Rate</p>
                    <p className="text-2xl font-bold text-foreground">{metrics?.conversion_rate}%</p>
                  </div>
                )}
                {metrics?.impressions && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Impressions</p>
                    <p className="text-2xl font-bold text-foreground">{metrics?.impressions?.toLocaleString()}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Content Body */}
          <div className="bg-muted rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Icon name="FileText" size={16} />
                Content
              </h3>
              <Button
                variant="ghost"
                size="sm"
                iconName="Copy"
                onClick={handleCopyContent}
              >
                Copy
              </Button>
            </div>
            <pre className="whitespace-pre-wrap text-sm text-foreground font-mono max-h-96 overflow-y-auto">
              {content?.content}
            </pre>
          </div>

          {/* Source Link */}
          {sourceUrl && (
            <div className="flex items-center justify-between p-4 bg-primary/5 border border-primary/20 rounded-xl">
              <div className="flex items-center gap-3">
                <Icon name="Link" size={20} color="var(--color-primary)" />
                <div>
                  <p className="text-sm font-semibold text-foreground">Source Material</p>
                  <p className="text-xs text-muted-foreground truncate max-w-md">{sourceUrl}</p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                iconName="ExternalLink"
                onClick={() => window.open(sourceUrl, '_blank')}
              >
                View Source
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentPreviewModal;